// Global Instructions Rule Applied!
import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import { Typography, Tooltip, Popover, Form, Badge, Row, Col, Tag } from 'antd'
import DatePicker from '../../../core/components/DatePicker.tsx'
import Button from '../../../core/components/Button.jsx'
import Select from '../../../core/components/Select.jsx'
import { FormSection } from '../../../core/components/FormSection.tsx'
import UserIconList from '../../../core/components/UserIconList.jsx'
import { AgentProvenanceTag } from '../../../core/components/AgentProvenanceTag.tsx'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faFilter,
  faPlus,
  faCalendarAlt,
  faComments,
  faEnvelope,
  faNoteSticky,
  faArrowsRotate,
  faExpand,
  faCompress,
  faRobot
} from '@fortawesome/free-solid-svg-icons'

import dayjs from 'dayjs'
import { useContactSearch } from '../../../core/hooks_deprecated/useContactSearch.js'
import { InteractionRecordFields, InteractionRecord } from '../../../state_manager/data_models/interaction_record.ts'
import { UserEntryFields } from '../../../state_manager/data_models/user_entry.ts'
import {
  fetchInteractionsForDeal,
  fetchInteractionsForAccount,
  fetchInteractionsForProspect
} from '../services/interactionsService.ts'
import { getAccessToken } from '../../../core/auth/authService'
import { store } from '../../../state_manager/store.ts'
import {
  selectInteractionsByEntityId,
  setInteractions
} from '../../../state_manager/state_update_actions/interactions/index.ts'
import { ShadowRichHtml } from '../../../core/components/ShadowRichHtml.tsx'

const { RangePicker } = DatePicker
const { Text } = Typography

// Interactions load one page at a time (most-recent first); more load on scroll.
// Previously the entire history shipped on open — ~10MB for busy accounts.
const PAGE_SIZE = 50

// One folded email thread on the timeline (see timelineItems below).
interface ThreadGroup {
  kind: 'group'
  id: string
  members: InteractionRecord[]
  size: number
  lowValueCount: number
  headId: string | null
}

type TimelineItem = { kind: 'single'; interaction: InteractionRecord } | ThreadGroup

interface FilterState {
  types: string[]
  participants: string[]
  participantNames: string[]
  dateRange: { start: string; end: string } | null
  showAllTypes: boolean
}

interface FilterPopoverProps {
  filters: FilterState
  onFiltersChange: (filters: FilterState) => void
  onClearFilters: () => void
  onClose?: () => void
}

const FilterPopover: React.FC<FilterPopoverProps> = ({ filters, onFiltersChange, onClearFilters, onClose }) => {
  const [form] = Form.useForm()
  const [selectedContacts, setSelectedContacts] = useState<Array<{ label: string; value: string }>>([])

  const {
    searchContacts,
    loading: contactSearchLoading,
    results: contactSearchResults,
    clearResults: clearContactResults
  } = useContactSearch()

  useEffect(() => {
    form.setFieldsValue({
      types: filters.types || [],
      participants: filters.participants || [],
      dateRange: filters.dateRange ? [dayjs(filters.dateRange.start), dayjs(filters.dateRange.end)] : null
    })

    if (filters.participantNames && filters.participants) {
      const contacts = filters.participantNames.map((name, index) => ({
        label: name,
        value: filters.participants[index]
      }))
      setSelectedContacts(contacts)
    } else {
      setSelectedContacts([])
    }
  }, [filters, form])

  const handleApplyFilters = async () => {
    try {
      const values = await form.validateFields()

      const dateRangeValue = values.dateRange as [dayjs.Dayjs, dayjs.Dayjs] | null
      const newFilters: FilterState = {
        types: values.types || [],
        participants: values.participants || [],
        participantNames: selectedContacts
          .filter((contact) => (values.participants || []).includes(contact.value))
          .map((contact) => contact.label),
        dateRange:
          dateRangeValue && dateRangeValue[0] && dateRangeValue[1]
            ? {
                start: dateRangeValue[0].format('YYYY-MM-DD'),
                end: dateRangeValue[1].format('YYYY-MM-DD')
              }
            : null,
        showAllTypes: !values.types || values.types.length === 0
      }

      onFiltersChange(newFilters)
      onClose?.()
    } catch (error) {
      // Filter validation failed - form will show validation errors
    }
  }

  const handleClearFilters = () => {
    form.setFieldsValue({
      types: [],
      participants: [],
      dateRange: null
    })
    setSelectedContacts([])
    onClearFilters()
    onClose?.()
  }

  return (
    <div className='w-80 p-2'>
      <div className='mb-2 p-1.5 bg-gray-100 rounded-lg'>
        <h4 className='text-sm font-semibold text-gray-800 m-0'>Filter Interactions</h4>
      </div>
      <Form
        form={form}
        layout='vertical'
        className='space-y-1'
        initialValues={{
          types: [],
          participants: [],
          dateRange: null
        }}
      >
        <Form.Item label='Interaction Types' name='types' className='mb-2'>
          <Select
            mode='multiple'
            size='middle'
            allowClear
            placeholder='Select interaction types...'
            options={[
              { label: 'Meeting', value: 'meeting' },
              { label: 'Email', value: 'email' },
              { label: 'Note', value: 'note' },
              { label: 'Agent note', value: 'agent_note' }
            ]}
          />
        </Form.Item>

        <Form.Item label='Participants' name='participants' className='mb-2'>
          <Select
            mode='multiple'
            size='middle'
            allowClear
            showSearch
            loading={contactSearchLoading}
            filterOption={false}
            options={[
              ...selectedContacts,
              ...contactSearchResults.filter(
                (result: any) => !selectedContacts.some((selected) => selected.value === result.value)
              )
            ]}
            onSearch={(value: string) => {
              if (value && value.length >= 2) {
                searchContacts(value)
              } else if (!value) {
                clearContactResults()
              }
            }}
            onSelect={(value: any, option: any) => {
              if (!selectedContacts.some((contact) => contact.value === value)) {
                setSelectedContacts((prev) => [...prev, option])
              }
            }}
            onDeselect={(value: any) => {
              setSelectedContacts((prev) => prev.filter((contact) => contact.value !== value))
            }}
            onClear={() => {
              clearContactResults()
              setSelectedContacts([])
            }}
            notFoundContent={
              contactSearchLoading ? (
                <div className='text-center py-2 text-gray-500'>Searching...</div>
              ) : (
                <div className='text-center py-2 text-gray-500'>
                  {contactSearchResults.length === 0 ? 'Type to search contacts' : 'No contacts found'}
                </div>
              )
            }
          />
        </Form.Item>

        <Form.Item label='Date range' name='dateRange' className='mb-2'>
          <RangePicker placeholder={['Start date', 'End date']} size='middle' className='w-full' format='YYYY-MM-DD' />
        </Form.Item>

        <div className='flex justify-end space-x-2 pt-2 mt-2 border-t border-gray-200'>
          <Button variant='cancel' size='small' onClick={handleClearFilters}>
            Clear
          </Button>
          <Button variant='primary' size='small' onClick={handleApplyFilters}>
            Apply
          </Button>
        </div>
      </Form>
    </div>
  )
}

interface InteractionsSectionProps {
  dealId?: string | null
  recordId?: string | null
  recordType?: 'deal' | 'company' | 'prospect'
  onInteractionSelect?: (interaction: InteractionRecord) => void
  selectedInteractionId?: string | null
  onAddInteraction?: () => void
  onRefreshRequest?: (refreshFn: () => Promise<void>) => void
  style?: React.CSSProperties
}

export function InteractionsSection({
  dealId,
  recordId,
  recordType = 'deal',
  onInteractionSelect,
  selectedInteractionId,
  onAddInteraction,
  onRefreshRequest,
  style = {}
}: InteractionsSectionProps) {
  // Use recordId if provided, otherwise fall back to dealId for backward compatibility
  const location = useLocation()
  const navigate = useNavigate()
  const entityId = recordId || dealId || ''
  const entityType = recordId ? recordType : 'deal'
  const [filterPopoverVisible, setFilterPopoverVisible] = useState(false)
  const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set())
  const [filters, setFilters] = useState<FilterState>({
    types: [],
    participants: [],
    participantNames: [],
    dateRange: null,
    showAllTypes: true
  })
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [hasMore, setHasMore] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const dispatch = useDispatch()
  const interactions = useSelector((state: any) => selectInteractionsByEntityId(state, entityId))
  const fetchedFiltersRef = useRef<string | null>(null)

  const getApiFilters = () => {
    const apiFilters: any = {}
    const allTypesSelected =
      filters.types?.length === 4 && ['meeting', 'email', 'note', 'agent_note'].every((t) => filters.types?.includes(t))
    if (filters.types && filters.types.length > 0 && !allTypesSelected) {
      apiFilters.interaction_types = filters.types
    }
    if (filters.participants && filters.participants.length > 0) {
      apiFilters.participant_ids = filters.participants
    }
    if (filters.dateRange) {
      apiFilters.date_from = filters.dateRange.start
      apiFilters.date_to = filters.dateRange.end
    }
    return apiFilters
  }

  // Fetch a single page (most-recent first) for the current entity + filters.
  const fetchPage = useCallback(
    (offset: number): Promise<InteractionRecord[]> => {
      const currentToken = getAccessToken(store.getState().auth)
      const apiFilters = getApiFilters()
      const pagination = { limit: PAGE_SIZE, offset }
      if (entityType === 'company') {
        return fetchInteractionsForAccount(entityId, apiFilters, currentToken, pagination)
      } else if (entityType === 'prospect') {
        return fetchInteractionsForProspect(entityId, apiFilters, currentToken, pagination)
      }
      return fetchInteractionsForDeal(entityId, apiFilters, currentToken, pagination)
    },
    // getApiFilters() reads these filter fields; rebuild the fetcher when they change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [entityId, entityType, filters.types, filters.participants, filters.dateRange]
  )

  // Initial load + reload on entity/filter change: replace with page 1.
  useEffect(() => {
    if (!entityId) {
      fetchedFiltersRef.current = null
      return
    }

    const apiFilters = getApiFilters()
    const cacheKey = `${entityId}:${JSON.stringify(apiFilters)}`

    if (fetchedFiltersRef.current === cacheKey) {
      return
    }

    fetchedFiltersRef.current = cacheKey
    setLoading(true)
    setError(null)
    fetchPage(0)
      .then((page) => {
        dispatch(setInteractions(entityId, page))
        setHasMore(page.length === PAGE_SIZE)
      })
      .catch((err) => {
        console.error('Error fetching interactions:', err)
        setError(err instanceof Error ? err.message : 'Failed to fetch interactions')
        fetchedFiltersRef.current = null
      })
      .finally(() => setLoading(false))
  }, [entityId, entityType, dispatch, filters.types, filters.participants, filters.dateRange, fetchPage])

  // Infinite scroll: append the next page when the user nears the bottom.
  const loadMore = useCallback(() => {
    if (!entityId || loadingMore || !hasMore) return
    const existing = selectInteractionsByEntityId(store.getState(), entityId)
    setLoadingMore(true)
    fetchPage(existing.length)
      .then((page) => {
        const current = selectInteractionsByEntityId(store.getState(), entityId)
        dispatch(setInteractions(entityId, [...current, ...page]))
        setHasMore(page.length === PAGE_SIZE)
      })
      .catch((err) => {
        console.error('Error loading more interactions:', err)
      })
      .finally(() => setLoadingMore(false))
  }, [entityId, loadingMore, hasMore, fetchPage, dispatch])

  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const el = e.currentTarget
      if (el.scrollHeight - el.scrollTop - el.clientHeight < 250) {
        loadMore()
      }
    },
    [loadMore]
  )

  const getParticipantInfo = (interaction: InteractionRecord) => {
    const participantsField = InteractionRecordFields.prog__interaction_participants
    const participants = interaction[participantsField]

    if (!participants || participants.length === 0) return []

    return participants.map((participant: any) => {
      if (participant && typeof participant === 'object') {
        return {
          id: participant[UserEntryFields.contacts__id] || participant.id,
          name:
            participant[UserEntryFields.prog__display_name] ||
            `${participant[UserEntryFields.contacts__first_name] || ''} ${participant[UserEntryFields.contacts__last_name] || ''}`.trim() ||
            participant[UserEntryFields.contacts__email] ||
            'Unknown'
        }
      }

      const contactId = typeof participant === 'string' ? participant : participant?.id
      return {
        id: contactId,
        name: contactId
      }
    })
  }

  const getInteractionIcon = (type: string | null | undefined) => {
    const iconMap: Record<string, { icon: any; color: string }> = {
      meeting: { icon: faCalendarAlt, color: 'text-blue-600' },
      email: { icon: faEnvelope, color: 'text-orange-600' },
      note: { icon: faNoteSticky, color: 'text-purple-600' },
      // Agent-authored timeline notes (design §8.E) — e.g. the pipeline
      // meeting's per-deal discussion excerpts.
      agent_note: { icon: faRobot, color: 'text-blue-600' }
    }

    return iconMap[type?.toLowerCase() || ''] || { icon: faComments, color: 'text-gray-600' }
  }

  const handleAddInteraction = () => {
    onAddInteraction?.()
  }

  const handleInteractionView = (interaction: InteractionRecord) => {
    onInteractionSelect?.(interaction)
  }

  // DEEP LINK — `?interaction=<id>` on this record's own form URL.
  //
  // An interaction has no form of its own; it lives in the parent's timeline.
  // So the agent-activity Open button lands on the PARENT and names the
  // interaction here, and this opens it exactly as a click on its card would —
  // by firing the SAME `onInteractionSelect`, which is what guarantees the two
  // routes end in identical state (detail panel open, card highlighted).
  //
  // Three guards, each load-bearing:
  //   * the URL must point at THIS record — inactive form tabs stay mounted
  //     (display:none) and the global location belongs to the ACTIVE tab, so
  //     without this every open timeline would grab the same id;
  //   * the list arrives asynchronously, so this waits for the interaction to
  //     exist rather than running once on mount;
  //   * once per id, so closing the detail panel does not have it immediately
  //     reopened, and a refetch does not yank the user back to a card they had
  //     moved on from.
  //
  // Once consumed, the `interaction` param is dropped from the URL (history
  // replace, other params kept). That is what lets the SAME interaction be
  // opened again later — an agent signal cited twice, or clicked after the
  // panel was closed: the param reappears, the location changes, and the
  // once-per-id guard has been cleared by its absence in between.
  const focusedRef = useRef<string | null>(null)
  useEffect(() => {
    if (!entityId) return
    const params = new URLSearchParams(location.search)
    const target = params.get('interaction')
    if (!target) {
      focusedRef.current = null
      return
    }
    if (focusedRef.current === target) return
    if (!location.pathname.includes(`/forms/full/${entityId}`)) return

    const match = interactions.find(
      (item: InteractionRecord) => item[InteractionRecordFields.interactions__id] === target
    )
    if (!match) return
    focusedRef.current = target
    onInteractionSelect?.(match)
    params.delete('interaction')
    const rest = params.toString()
    navigate(`${location.pathname}${rest ? `?${rest}` : ''}`, { replace: true })
  }, [entityId, location.pathname, location.search, interactions, onInteractionSelect, navigate])

  const handleCardExpansion = (interactionId: string | null | undefined) => {
    if (!interactionId) return
    setExpandedCards((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(interactionId)) {
        newSet.delete(interactionId)
      } else {
        newSet.add(interactionId)
      }
      return newSet
    })
  }

  const handleFiltersChange = (newFilters: FilterState) => {
    setFilters(newFilters)
    setFilterPopoverVisible(false)
  }

  const handleClearFilters = () => {
    setFilters({
      types: [],
      participants: [],
      participantNames: [],
      dateRange: null,
      showAllTypes: true
    })
    setFilterPopoverVisible(false)
  }

  const handleRefresh = async () => {
    if (!entityId) return

    fetchedFiltersRef.current = null
    setLoading(true)
    setError(null)

    try {
      const currentToken = getAccessToken(store.getState().auth)
      if (!currentToken) {
        setError('No access token available')
        return
      }

      const page = await fetchPage(0)
      dispatch(setInteractions(entityId, page))
      setHasMore(page.length === PAGE_SIZE)
      const apiFilters = getApiFilters()
      fetchedFiltersRef.current = `${entityId}:${JSON.stringify(apiFilters)}`
    } catch (err) {
      console.error('Error refreshing interactions:', err)
      setError(err instanceof Error ? err.message : 'Failed to refresh interactions')
      fetchedFiltersRef.current = null
    } finally {
      setLoading(false)
    }
  }

  const refreshRef = useRef<(() => Promise<void>) | null>(null)
  refreshRef.current = handleRefresh

  useEffect(() => {
    if (onRefreshRequest && refreshRef.current) {
      onRefreshRequest(refreshRef.current)
    }
  }, [onRefreshRequest])

  const getDateTimeString = (interaction: InteractionRecord) => {
    const datetimeField = InteractionRecordFields.interactions__datetime
    const datetime = interaction[datetimeField] as string | undefined

    if (!datetime) {
      return ''
    }

    return datetime
  }

  const sortedInteractions = useMemo(() => {
    return [...interactions].sort((a, b) => {
      const dateTimeA = getDateTimeString(a)
      const dateTimeB = getDateTimeString(b)

      const timestampA = dateTimeA ? new Date(dateTimeA).getTime() : 0
      const timestampB = dateTimeB ? new Date(dateTimeB).getTime() : 0

      return timestampB - timestampA
    })
  }, [interactions])

  const notesField = InteractionRecordFields.interactions__notes

  // Email threads (20261204120100): the timeline RPC stamps every email that
  // shares a subject thread with others (a gap of cfg thread_gap_days splits
  // a thread) with prog__thread_group_id / prog__thread_size /
  // prog__thread_latest_substantive_id / prog__thread_low_value_count. Rows
  // sharing a group id fold into ONE card positioned at the latest member,
  // headed by the latest substantive message (low-value kinds — logistics,
  // acknowledgements, auto-replies — sit inside the group until expanded).
  // A thread split across the 50-row page boundary renders as two groups
  // until the next page loads; the server-side size is what the card shows.
  const timelineItems = useMemo((): TimelineItem[] => {
    const groups = new Map<string, ThreadGroup>()
    const items: TimelineItem[] = []
    for (const interaction of sortedInteractions) {
      const groupId = interaction[InteractionRecordFields.prog__thread_group_id] as string | null | undefined
      if (!groupId) {
        items.push({ kind: 'single', interaction })
        continue
      }
      const existing = groups.get(groupId)
      if (existing) {
        existing.members.push(interaction)
        continue
      }
      const group: ThreadGroup = {
        kind: 'group',
        id: `thread:${groupId}`,
        members: [interaction],
        size: (interaction[InteractionRecordFields.prog__thread_size] as number | null | undefined) || 1,
        lowValueCount:
          (interaction[InteractionRecordFields.prog__thread_low_value_count] as number | null | undefined) || 0,
        headId: (interaction[InteractionRecordFields.prog__thread_latest_substantive_id] as string | null | undefined) || null
      }
      groups.set(groupId, group)
      items.push(group)
    }
    return items
  }, [sortedInteractions])

  const groupHead = (group: ThreadGroup): InteractionRecord =>
    group.members.find((m) => m[InteractionRecordFields.interactions__id] === group.headId) || group.members[0]

  const hasBody = (i: InteractionRecord) =>
    Boolean(
      (i[notesField] && (i[notesField] as string)?.replace(/<[^>]*>?/g, '')) ||
        // agent_note bodies live in description (plain text, long)
        (i[InteractionRecordFields.interactions__type] === 'agent_note' &&
          ((i[InteractionRecordFields.interactions__description] as string) || '').length > 280)
    )

  const expandableIds = useMemo(
    () =>
      new Set(
        timelineItems.flatMap((item) =>
          item.kind === 'group'
            ? [item.id]
            : item.interaction[InteractionRecordFields.interactions__id] && hasBody(item.interaction)
              ? [item.interaction[InteractionRecordFields.interactions__id] as string]
              : []
        )
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [timelineItems]
  )

  // Selecting a message that sits inside a collapsed thread (deep link,
  // detail panel navigation) opens its group so the highlight is visible.
  useEffect(() => {
    if (!selectedInteractionId) return
    const owner = timelineItems.find(
      (item) =>
        item.kind === 'group' &&
        item.members.some((m) => m[InteractionRecordFields.interactions__id] === selectedInteractionId)
    )
    if (owner && owner.kind === 'group' && !expandedCards.has(owner.id)) {
      setExpandedCards((prev) => new Set(prev).add(owner.id))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedInteractionId, timelineItems])

  const allExpanded = expandableIds.size > 0 && [...expandableIds].every((id) => expandedCards.has(id))
  const handleExpandCollapseAll = () => {
    if (allExpanded) {
      setExpandedCards(new Set())
    } else {
      setExpandedCards(new Set(expandableIds))
    }
  }

  const QUICK_FILTER_TYPES = ['meeting', 'email', 'note', 'agent_note'] as const
  const isQuickFilterActive = (type: string) => filters.types?.includes(type) ?? false

  const toggleQuickFilter = (type: string) => {
    setFilters((prev) => {
      const current = prev.types ?? []
      const hasType = current.includes(type)
      const newTypes = hasType ? current.filter((t) => t !== type) : [...current, type]
      return {
        ...prev,
        types: newTypes,
        showAllTypes: newTypes.length === 0 || newTypes.length === QUICK_FILTER_TYPES.length
      }
    })
  }

  const datePill = (interaction: InteractionRecord) => {
    const dateTimeString = getDateTimeString(interaction)
    if (!dateTimeString) return null
    return (
      <div
        style={{ fontSize: '10px', paddingLeft: 2, paddingRight: 2, paddingTop: 2, paddingBottom: 2 }}
        className='bg-blue-100 text-blue-600 border border-blue-300 py-0.5 h-5 rounded-md font-medium flex items-center justify-center'
      >
        {dayjs(dateTimeString).format('MM/DD/YY')}
      </div>
    )
  }

  // The one-line summary. Empty + queued means the summarizer has not
  // written it yet: show the placeholder rather than a blank line. It is
  // never written INTO description — the queue's trigger keys on description
  // being empty.
  const renderDescription = (interaction: InteractionRecord, isExpanded: boolean) => {
    const description = (interaction[InteractionRecordFields.interactions__description] as string) || ''
    if (interaction[InteractionRecordFields.interactions__type] === 'agent_note') {
      // Agent-note bodies are plain text with newlines (transcript excerpts)
      // — preserve line breaks and clamp until expanded.
      return (
        <div className='text-xs text-gray-700 whitespace-pre-line'>
          {isExpanded || description.length <= 280 ? description : `${description.slice(0, 280)}…`}
        </div>
      )
    }
    if (!description) {
      const status = interaction[InteractionRecordFields.prog__summary_status]
      if (status === 'pending' || status === 'processing') {
        return (
          <div className='text-xs'>
            <Text type='secondary' italic>
              Summary pending…
            </Text>
          </div>
        )
      }
    }
    return <div className='text-xs text-gray-700'>{description}</div>
  }

  const renderCard = (interaction: InteractionRecord, options: { nested?: boolean } = {}) => {
    const interactionId = interaction[InteractionRecordFields.interactions__id]
    const interactionType = interaction[InteractionRecordFields.interactions__type]
    const { icon, color } = getInteractionIcon(interactionType)
    const participantInfo = getParticipantInfo(interaction)
    const isExpanded = interactionId ? expandedCards.has(interactionId) : false
    const isSelected = selectedInteractionId === interactionId
    const isLowValue = Boolean(interaction[InteractionRecordFields.prog__content_kind_is_low_value])
    const kindLabel = interaction[InteractionRecordFields.prog__content_kind_label]

    return (
      <div
        key={interactionId}
        data-debug={`interaction-wrapper-${interactionId}`}
        data-interaction-id={interactionId}
        data-interaction-type={interactionType}
        className={`relative flex items-start space-x-2 group ${isSelected ? 'selected' : ''} ${
          options.nested && isLowValue && !isSelected ? 'opacity-70' : ''
        }`}
      >
        <div data-debug={`interaction-node-${interactionId}`} className='flex-shrink-0 relative z-10'>
          <div
            data-debug={`interaction-icon-${interactionId}`}
            className={`${options.nested ? 'w-6 h-6' : 'w-8 h-8'} rounded-full bg-white border-2 border-gray-300 flex items-center justify-center shadow-sm transition-all duration-200 ${color} ${
              isSelected ? 'border-blue-500 ring-2 ring-blue-100' : ''
            }`}
          >
            <FontAwesomeIcon icon={icon} size='xs' />
          </div>
        </div>

        <div
          data-debug={`interaction-content-${interactionId}`}
          className={`flex-1 min-w-0 pb-1 cursor-pointer hover:border-blue-400 transition-colors ${
            isSelected
              ? 'bg-blue-50 border border-blue-200 rounded-md p-1.5 shadow-sm'
              : 'bg-white border border-gray-200 rounded-md p-1.5 hover:shadow-md'
          }`}
          onClick={() => handleInteractionView(interaction)}
        >
          <div className='space-y-0.5'>
            <Row gutter={[8, 8]} align='middle' wrap>
              <Col xs={24} lg={{ flex: '1 1 auto' }}>
                <p className='text-[14px] font-semibold text-gray-900 m-0'>
                  {interaction[InteractionRecordFields.interactions__subject] || ''}
                </p>
              </Col>
              <Col xs={24} lg={{ flex: '0 0 auto' }}>
                <Row gutter={[8, 8]} align='middle' wrap>
                  {/*
                    Agent-created marker, leading the row's right-hand
                    cluster. It sits here rather than beside the subject
                    because the subject Col is `1 1 auto` and subjects run
                    long, and because grouping it with the avatars keeps the
                    date pill hard right. `compact` because the full sentence
                    (~374px) is wider than this whole cluster -- the Tooltip
                    carries it. The explicit guard avoids rendering an empty
                    Col that would still consume an 8px gutter on every row.
                  */}
                  {interaction[InteractionRecordFields.prog__created_provenance] && (
                    <Col flex='none'>
                      <AgentProvenanceTag
                        provenance={interaction[InteractionRecordFields.prog__created_provenance]}
                        compact
                      />
                    </Col>
                  )}
                  {options.nested && isLowValue && kindLabel && (
                    <Col flex='none'>
                      <Tag className='m-0 text-[10px] leading-4'>{kindLabel}</Tag>
                    </Col>
                  )}
                  {participantInfo.length > 0 && (
                    <Col flex='none'>
                      <UserIconList
                        contactNames={participantInfo.map((participant) => participant.name)}
                        contactIds={participantInfo.map((participant) => participant.id)}
                        maxVisible={5}
                        size='medium'
                      />
                    </Col>
                  )}
                  {interaction[InteractionRecordFields.interactions__datetime] && (
                    <Col flex='none'>{datePill(interaction)}</Col>
                  )}
                </Row>
              </Col>
            </Row>

            {renderDescription(interaction, isExpanded)}

            {isExpanded && interaction[notesField] && (
              <div className='mt-1 p-1.5 bg-gray-50 rounded border'>
                <ShadowRichHtml className='text-xs' html={interaction[notesField]} />
              </div>
            )}

            {hasBody(interaction) ? (
              <div className='mt-1'>
                <button
                  className='text-xs text-blue-600 hover:text-blue-800 hover:underline cursor-pointer'
                  onClick={(e) => {
                    e.stopPropagation()
                    handleCardExpansion(interactionId)
                  }}
                >
                  {isExpanded ? 'View less' : 'View more'}
                </button>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    )
  }

  // One folded thread: the same card chrome as a single email, headed by the
  // latest substantive message, with the message count and the low-value
  // count as Tags in the right-hand cluster. Expanding lists the members as
  // ordinary (smaller) cards; each keeps its own View more for the body.
  const renderGroup = (group: ThreadGroup) => {
    const head = groupHead(group)
    const latest = group.members[0]
    const isExpanded = expandedCards.has(group.id)
    const memberIds = new Set(group.members.map((m) => m[InteractionRecordFields.interactions__id]))
    const isSelected = Boolean(selectedInteractionId && memberIds.has(selectedInteractionId))
    const { icon, color } = getInteractionIcon(head[InteractionRecordFields.interactions__type])
    const seen = new Set<string>()
    const participants = group.members
      .flatMap((m) => getParticipantInfo(m))
      .filter((p) => {
        if (!p.id || seen.has(p.id)) return false
        seen.add(p.id)
        return true
      })

    return (
      <div
        key={group.id}
        data-debug={`interaction-thread-${group.id}`}
        data-thread-group-id={group.id}
        data-thread-size={group.size}
        className={`relative flex items-start space-x-2 group ${isSelected && !isExpanded ? 'selected' : ''}`}
      >
        <div className='flex-shrink-0 relative z-10'>
          <div
            className={`w-8 h-8 rounded-full bg-white border-2 border-gray-300 flex items-center justify-center shadow-sm transition-all duration-200 ${color} ${
              isSelected && !isExpanded ? 'border-blue-500 ring-2 ring-blue-100' : ''
            }`}
          >
            <FontAwesomeIcon icon={icon} size='xs' />
          </div>
        </div>

        <div className='flex-1 min-w-0 pb-1'>
          <div
            className={`cursor-pointer hover:border-blue-400 transition-colors ${
              isSelected && !isExpanded
                ? 'bg-blue-50 border border-blue-200 rounded-md p-1.5 shadow-sm'
                : 'bg-white border border-gray-200 rounded-md p-1.5 hover:shadow-md'
            }`}
            onClick={() => handleInteractionView(head)}
          >
            <div className='space-y-0.5'>
              <Row gutter={[8, 8]} align='middle' wrap>
                <Col xs={24} lg={{ flex: '1 1 auto' }}>
                  <p className='text-[14px] font-semibold text-gray-900 m-0'>
                    {head[InteractionRecordFields.interactions__subject] || ''}
                  </p>
                </Col>
                <Col xs={24} lg={{ flex: '0 0 auto' }}>
                  <Row gutter={[8, 8]} align='middle' wrap>
                    <Col flex='none'>
                      <Tag color='blue' className='m-0 text-[10px] leading-4'>
                        {group.size} messages
                      </Tag>
                    </Col>
                    {group.lowValueCount > 0 && (
                      <Col flex='none'>
                        <Tooltip title='Scheduling, acknowledgements, automated mail — shown when the thread is expanded'>
                          <Tag className='m-0 text-[10px] leading-4'>{group.lowValueCount} low-value</Tag>
                        </Tooltip>
                      </Col>
                    )}
                    {participants.length > 0 && (
                      <Col flex='none'>
                        <UserIconList
                          contactNames={participants.map((participant) => participant.name)}
                          contactIds={participants.map((participant) => participant.id)}
                          maxVisible={5}
                          size='medium'
                        />
                      </Col>
                    )}
                    {latest[InteractionRecordFields.interactions__datetime] && (
                      <Col flex='none'>{datePill(latest)}</Col>
                    )}
                  </Row>
                </Col>
              </Row>

              {renderDescription(head, false)}

              <div className='mt-1'>
                <button
                  className='text-xs text-blue-600 hover:text-blue-800 hover:underline cursor-pointer'
                  onClick={(e) => {
                    e.stopPropagation()
                    handleCardExpansion(group.id)
                  }}
                >
                  {isExpanded ? 'Hide messages' : `View ${group.size} messages`}
                </button>
              </div>
            </div>
          </div>

          {isExpanded && (
            <div data-debug={`interaction-thread-members-${group.id}`} className='ml-3 mt-1 space-y-1'>
              {group.members.map((member) => renderCard(member, { nested: true }))}
            </div>
          )}
        </div>
      </div>
    )
  }

  const headerActions = (
    <div className='flex items-center gap-1 flex-shrink-0'>
      {QUICK_FILTER_TYPES.map((type) => {
        const iconMap: Record<string, typeof faCalendarAlt> = {
          meeting: faCalendarAlt,
          email: faEnvelope,
          note: faNoteSticky,
          agent_note: faRobot
        }
        const labelMap: Record<string, string> = {
          meeting: 'Meetings',
          email: 'Emails',
          note: 'Notes',
          agent_note: 'Agent notes'
        }
        const isActive = isQuickFilterActive(type)
        return (
          <Tooltip key={type} title={labelMap[type]}>
            <Button
              variant='default'
              size='small'
              icon={<FontAwesomeIcon icon={iconMap[type]} style={{ color: isActive ? '#2563eb' : '#9ca3af' }} />}
              style={{ color: isActive ? '#2563eb' : '#9ca3af' }}
              onClick={() => toggleQuickFilter(type)}
            />
          </Tooltip>
        )
      })}
      <Popover
        content={
          <FilterPopover
            filters={filters}
            onFiltersChange={handleFiltersChange}
            onClearFilters={handleClearFilters}
            onClose={() => setFilterPopoverVisible(false)}
          />
        }
        title={null}
        trigger='click'
        placement='bottomRight'
        overlayClassName='interaction-filter-popover'
        open={filterPopoverVisible}
        onOpenChange={setFilterPopoverVisible}
      >
        <Badge
          count={(filters.types?.length || 0) + (filters.participants?.length || 0) + (filters.dateRange ? 1 : 0)}
          size='small'
          offset={[0, 0]}
        >
          <Tooltip title='Filter interactions'>
            <Button
              variant='default'
              size='small'
              icon={
                <FontAwesomeIcon
                  icon={faFilter}
                  style={{
                    color:
                      (filters.types?.length || 0) + (filters.participants?.length || 0) + (filters.dateRange ? 1 : 0)
                        ? '#2563eb'
                        : '#9ca3af'
                  }}
                />
              }
              style={{
                color:
                  (filters.types?.length || 0) + (filters.participants?.length || 0) + (filters.dateRange ? 1 : 0)
                    ? '#2563eb'
                    : '#9ca3af'
              }}
            />
          </Tooltip>
        </Badge>
      </Popover>
      <Tooltip title={allExpanded ? 'Collapse all' : 'Expand all'}>
        <Button
          variant='default'
          size='small'
          icon={
            <FontAwesomeIcon
              icon={allExpanded ? faCompress : faExpand}
              style={{
                color: expandableIds.size === 0 ? '#d1d5db' : allExpanded ? '#2563eb' : '#9ca3af'
              }}
            />
          }
          style={{
            color: expandableIds.size === 0 ? '#d1d5db' : allExpanded ? '#2563eb' : '#9ca3af'
          }}
          disabled={expandableIds.size === 0}
          onClick={handleExpandCollapseAll}
        />
      </Tooltip>
      <Tooltip title='Refresh interactions'>
        <Button
          variant='default'
          size='small'
          icon={<FontAwesomeIcon icon={faArrowsRotate} style={{ color: '#9ca3af' }} />}
          style={{ color: '#9ca3af' }}
          loading={loading}
          onClick={handleRefresh}
        />
      </Tooltip>
      <Tooltip title='Add new interaction'>
        <Button
          variant='primary'
          size='small'
          className='text-xs px-2 py-1 h-6'
          icon={<FontAwesomeIcon icon={faPlus} className='text-xs' />}
          onClick={handleAddInteraction}
        />
      </Tooltip>
    </div>
  )

  return (
    <FormSection
      title='Interactions'
      titleColor='purple'
      headerActions={
        <div className='flex items-center gap-1.5 flex-shrink-0 min-w-0'>
          {!selectedInteractionId && (
            <div
              data-debug='interactions-count-display'
              className='text-xs text-gray-500 whitespace-nowrap flex-shrink-0 hidden lg:block'
            >
              {interactions.length}
              {hasMore ? '+' : ''} interactions
            </div>
          )}
          {headerActions}
        </div>
      }
      loading={loading}
      className='shadow-sm border border-gray-200 h-full flex flex-col'
      styles={{
        body: {
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
          paddingBottom: 0
        }
      }}
    >
      <div
        data-debug='interactions-timeline-container'
        data-component='InteractionsSection'
        data-record-id={entityId}
        data-record-type={entityType}
        className='relative flex-1 overflow-y-auto min-h-0 mt-2 pl-2 interactions-timeline-container'
        onScroll={handleScroll}
      >
        {error ? (
          <div data-debug='interactions-error-state' className='text-center py-8'>
            <Text type='danger'>{error}</Text>
          </div>
        ) : sortedInteractions.length === 0 ? (
          <div data-debug='interactions-empty-state' className='text-center py-8'>
            <Text type='secondary'>
              {(filters.types && filters.types.length > 0) ||
              (filters.participants && filters.participants.length > 0) ||
              filters.dateRange
                ? 'No interactions match the current filters'
                : 'No interactions yet'}
            </Text>
            {((filters.types && filters.types.length > 0) ||
              (filters.participants && filters.participants.length > 0) ||
              filters.dateRange) && (
              <div className='mt-2'>
                <Button
                  variant='default'
                  type='link'
                  size='small'
                  onClick={handleClearFilters}
                  className='text-blue-600'
                >
                  Clear filters to see all interactions
                </Button>
              </div>
            )}
          </div>
        ) : (
          <div data-debug='interactions-timeline-content' className='relative'>
            <div data-debug='timeline-line' className='absolute left-3 top-3 bottom-3 w-0.5 bg-gray-300'></div>

            <div data-debug='interactions-list' className='space-y-1 interactions-container'>
              {timelineItems.map((item) =>
                item.kind === 'group' ? renderGroup(item) : renderCard(item.interaction)
              )}
            </div>
            {loadingMore && <div className='text-center py-3 text-xs text-gray-400'>Loading more…</div>}
          </div>
        )}
      </div>
    </FormSection>
  )
}
